'use client'

import { motion } from 'framer-motion'
import { CheckCircle, ArrowRight } from 'lucide-react'
import Link from 'next/link'
import { ReactNode } from 'react'

interface ServiceItem {
  icon: ReactNode
  title: string
  description: string
  features: string[]
}

interface TechItem {
  name: string
  category: string
}

interface ProcessStep {
  step: string
  title: string
  description: string
}

interface ServicePageProps {
  title: string
  titleHighlight: string
  description: string
  stats: { value: string; label: string }[]
  services: ServiceItem[]
  technologies: TechItem[]
  process: ProcessStep[]
  accentColor?: string // tailwind gradient like 'from-sky-500 to-blue-600'
}

export default function ServicePageTemplate({
  title, titleHighlight, description, stats, services, technologies, process,
  accentColor = 'from-sky-500 to-blue-600'
}: ServicePageProps) {
  return (
    <main className="min-h-screen pt-20">
      {/* Hero */}
      <section className="py-12 sm:py-16 bg-gradient-to-br from-sky-50 via-white to-blue-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-sky-100/30 rounded-full blur-3xl hidden sm:block" />
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} className="text-center lg:text-left">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
                {title}
                <span className={`block bg-gradient-to-r ${accentColor} bg-clip-text text-transparent`}>
                  {titleHighlight}
                </span>
              </h1>
              <p className="text-base sm:text-lg text-slate-500 mb-6 sm:mb-8 leading-relaxed">{description}</p>
              <div className="flex flex-col xs:flex-row flex-wrap gap-3 justify-center lg:justify-start">
                <Link href="/contact" className="btn-primary inline-flex items-center">
                  Start Your Project <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
                <Link href="/contact" className="btn-secondary">View Portfolio</Link>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
              <div className="bg-white rounded-2xl p-6 shadow-xl shadow-sky-100/50 border border-sky-100/50 relative z-10">
                <div className="grid grid-cols-2 gap-5">
                  {stats.map((stat, i) => (
                    <div key={i} className="text-center">
                      <div className={`text-2xl font-bold bg-gradient-to-r ${accentColor} bg-clip-text text-transparent mb-1`}>{stat.value}</div>
                      <div className="text-sm text-slate-500">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">What We Offer</h2>
            <p className="text-slate-500 max-w-2xl mx-auto">Comprehensive services from concept to deployment</p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-5">
            {services.map((service, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.08 }} viewport={{ once: true }}
                className="bg-sky-50/50 rounded-2xl p-6 hover:shadow-lg hover:shadow-sky-100/50 transition-all duration-300 border border-sky-100/50 group">
                <div className={`w-12 h-12 bg-gradient-to-r ${accentColor} rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform`}>
                  {service.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{service.title}</h3>
                <p className="text-sm text-slate-500 mb-4">{service.description}</p>
                <ul className="space-y-1.5">
                  {service.features.map((f, i) => (
                    <li key={i} className="flex items-center text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mr-2 flex-shrink-0" />{f}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-20 bg-sky-50/30">
        <div className="container mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Technologies</h2>
            <p className="text-slate-500">Cutting-edge tools for modern solutions</p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {technologies.map((tech, i) => (
              <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.05 }} viewport={{ once: true }}
                whileHover={{ y: -3 }}
                className="bg-white rounded-xl p-4 text-center shadow-sm border border-sky-100/50 hover:shadow-md hover:border-sky-200 transition-all">
                <h4 className="font-semibold text-slate-900 text-sm">{tech.name}</h4>
                <p className="text-[11px] text-slate-400 mt-0.5">{tech.category}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Our Process</h2>
            <p className="text-slate-500">Systematic approach ensuring quality at every step</p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {process.map((step, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} viewport={{ once: true }}
                className="bg-gradient-to-br from-sky-50/50 to-blue-50/50 rounded-2xl p-6 border border-sky-100/50">
                <div className={`text-2xl font-bold bg-gradient-to-r ${accentColor} bg-clip-text text-transparent mb-3`}>{step.step}</div>
                <h3 className="font-bold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-sm text-slate-500">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={`py-16 bg-gradient-to-r ${accentColor} relative overflow-hidden`}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.1),transparent)] pointer-events-none" />
        <div className="container mx-auto px-6 text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl font-bold text-white mb-4">Ready to Get Started?</h2>
            <p className="text-white/80 mb-6 max-w-2xl mx-auto">
              Let's create a powerful solution that drives your business forward.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link href="/contact" className="bg-white text-slate-900 px-7 py-3 rounded-xl font-semibold hover:shadow-xl transition-all text-sm">
                Get Free Quote
              </Link>
              <Link href="/contact" className="border-2 border-white text-white px-7 py-3 rounded-xl font-semibold hover:bg-white hover:text-slate-900 transition-all text-sm">
                Schedule Call
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  )
}
